import React, { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Switch } from "../ui/switch";
import { formatDate } from "./constants";

export function EmailsTab({ contractId, emailLogs, fetchData, expanded }) {
  const [form, setForm] = useState({ subject: "", message: "", send_to_supplier: true, send_to_owner: true });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSend = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ subject: form.subject, message: form.message, send_to_supplier: form.send_to_supplier.toString(), send_to_owner: form.send_to_owner.toString() }).toString();
      await apiClient.post(`/contracts/${contractId}/send-notification?${params}`);
      toast.success("Notification sent");
      setForm({ subject: "", message: "", send_to_supplier: true, send_to_owner: true });
      setShowForm(false);
      fetchData();
    } catch (e) {
      toast.error("Failed to send");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{emailLogs.length} email(s) sent</p>
        <Button size="sm" variant="outline" className="gap-1 rounded-sm" onClick={() => setShowForm(true)} data-testid="send-email-btn"><Send size={14} />Send Notification</Button>
      </div>

      {showForm && (
        <Card className="border-cyan-200">
          <CardContent className="pt-4 space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-sm h-9" placeholder="Email subject" data-testid="email-subject-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Message</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="rounded-sm" placeholder="Email message body..." data-testid="email-message-input" />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch checked={form.send_to_supplier} onCheckedChange={(v) => setForm({ ...form, send_to_supplier: v })} />
                <Label className="text-xs">Send to Supplier</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.send_to_owner} onCheckedChange={(v) => setForm({ ...form, send_to_owner: v })} />
                <Label className="text-xs">Send to Contract Owner</Label>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" disabled={saving || !form.subject || !form.message} onClick={handleSend} data-testid="send-email-submit-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} className="mr-1" />Send</>}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className={`space-y-2 overflow-y-auto ${expanded ? "max-h-[calc(100vh-350px)]" : "max-h-[400px]"}`}>
        {emailLogs.map((email) => (
          <div key={email.id} className="p-3 border rounded-sm">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-sm">{email.subject || "(No subject)"}</span>
              <Badge variant="outline" className="text-xs">{email.status}</Badge>
            </div>
            <p className="text-xs text-slate-500">To: {email.to || "(unknown)"}</p>
            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{email.body}</p>
            <p className="text-xs text-slate-400 mt-1">{formatDate(email.sent_at)}</p>
          </div>
        ))}
        {emailLogs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No emails sent yet</p>}
      </div>
    </div>
  );
}
